import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Input } from '@/shared/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'

export interface TaskFilterOption {
  id: number
  name: string
}

export type TaskFilterCommitMode = 'on-select' | 'explicit'
export type TaskFilterMassActions = 'show-all' | 'select-deselect'

interface Props {
  options: TaskFilterOption[]
  selectedIds: number[]
  /**
   * Default mode: called on each tick and on Show All. Explicit mode: not called by the
   * filter; a sibling Apply control commits the draft.
   */
  onApply: (selectedIds: number[]) => void
  /**
   * When `commitMode` is `explicit`, notified whenever the internal draft changes so a sibling
   * Apply control can commit the draft.
   */
  onDraftChange?: (draftIds: number[]) => void
  /**
   * Default `on-select` (Dashboard / Maintenance Logs / report detail).
   * Instrument → Reports uses `explicit`.
   */
  commitMode?: TaskFilterCommitMode
  /** Default `show-all`. Reports uses `select-deselect` (Select All checkbox, same chrome). */
  massActions?: TaskFilterMassActions
  /** Default false. Reports enables search. */
  enableSearch?: boolean
  id?: string
  className?: string
}

const hasSameIds = (first: number[], second: number[]) =>
  first.length === second.length && first.every((taskId) => second.includes(taskId))

const matchesSearch = (taskName: string, query: string) =>
  taskName.toLowerCase().includes(query.trim().toLowerCase())

// "Show All" means no task scoping, which is why this is not a MultiSelectCombobox:
// its "Select all" ticks every id instead of clearing the filter.
export const TaskFilter = ({
  options,
  selectedIds,
  onApply,
  onDraftChange,
  commitMode = 'on-select',
  massActions = 'show-all',
  enableSearch = false,
  id,
  className,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [draftIds, setDraftIds] = useState<number[]>(selectedIds)
  const [searchQuery, setSearchQuery] = useState('')
  const openRef = useRef(false)
  const lastAppliedIdsRef = useRef<number[]>(selectedIds)

  const updateDraftIds = (nextIds: number[]) => {
    setDraftIds(nextIds)
    onDraftChange?.(nextIds)
  }

  const setPopoverOpen = (nextOpen: boolean) => {
    openRef.current = nextOpen
    setOpen(nextOpen)
  }

  // Compared against the last applied ids, not selectedIds, so a repeat selection made
  // before the parent echoes selectedIds back does not apply twice.
  const applyIds = (nextIds: number[]) => {
    updateDraftIds(nextIds)
    if (hasSameIds(nextIds, lastAppliedIdsRef.current)) return
    lastAppliedIdsRef.current = nextIds
    onApply(nextIds)
  }

  // Explicit mode only drafts; a sibling Apply control commits.
  const changeSelection = (nextIds: number[]) => {
    if (commitMode === 'explicit') {
      updateDraftIds(nextIds)
      return
    }
    applyIds(nextIds)
  }

  const selectedIdsKey = selectedIds.join(',')
  useEffect(() => {
    // A late parent echo must not wipe a newer tick while the list is still open.
    if (
      commitMode !== 'explicit' &&
      openRef.current &&
      !hasSameIds(selectedIds, lastAppliedIdsRef.current)
    ) {
      return
    }
    lastAppliedIdsRef.current = selectedIds
    updateDraftIds(selectedIds)
    // Sync on value identity, not array reference: an inline [] must not reset the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey, commitMode])

  const handleOpenChange = (nextOpen: boolean) => {
    setPopoverOpen(nextOpen)
    setSearchQuery('')
    // Explicit mode (AC7): discard any abandoned draft when the list opens again so
    // a later Apply cannot commit a stale selection the user never re-confirmed.
    // Draft is intentionally kept on close so sibling Apply still works with the
    // dropdown shut (legacy filters-calendar: select → close → Apply).
    if (nextOpen) updateDraftIds(selectedIds)
  }

  const toggleTask = (taskId: number) => {
    changeSelection(
      draftIds.includes(taskId)
        ? draftIds.filter((previousId) => previousId !== taskId)
        : [...draftIds, taskId],
    )
  }

  // Show All applies immediately in every mode.
  const showAll = () => {
    setSearchQuery('')
    applyIds([])
    setPopoverOpen(false)
  }

  const filteredOptions = enableSearch
    ? options.filter((option) => matchesSearch(option.name, searchQuery))
    : options

  const visibleIds = filteredOptions.map((option) => option.id)
  const isSelectAllActive =
    visibleIds.length > 0 && visibleIds.every((taskId) => draftIds.includes(taskId))

  // Same chrome as Dashboard/ML "Show All" (consistency). Toggle: select all visible, or
  // clear visible ids when already fully selected (JW 582 select + deselect without twin buttons).
  const toggleSelectAllVisible = () => {
    if (visibleIds.length === 0) return
    if (isSelectAllActive) {
      const visibleIdSet = new Set(visibleIds)
      changeSelection(draftIds.filter((taskId) => !visibleIdSet.has(taskId)))
      return
    }
    changeSelection([...new Set([...draftIds, ...visibleIds])])
  }

  // Both modes show the draft on the trigger immediately. Explicit mode still waits for
  // Apply before the calendar changes; on-select has already committed that draft.
  const triggerIds = draftIds
  const triggerOptions = options.filter((option) => triggerIds.includes(option.id))
  const singleSelectedName = triggerIds.length === 1 ? triggerOptions[0]?.name : undefined
  const triggerLabel =
    triggerIds.length === 0
      ? t('shared.taskFilter.allTasks')
      : (singleSelectedName ?? t('shared.taskFilter.selectedCount', { count: triggerIds.length }))
  const isShowAllActive = draftIds.length === 0
  const isMassActionActive = massActions === 'show-all' ? isShowAllActive : isSelectAllActive
  const massActionLabel =
    massActions === 'show-all' ? t('shared.taskFilter.showAll') : t('shared.taskFilter.selectAll')
  const onMassActionClick = massActions === 'show-all' ? showAll : toggleSelectAllVisible

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          size="sm"
          type="button"
          title={singleSelectedName}
          className={cn('w-44 justify-between text-sm font-normal', className)}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-2">
        {enableSearch && (
          <div className="mb-2">
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder={t('shared.taskFilter.searchPlaceholder')}
              aria-label={t('shared.taskFilter.searchAriaLabel')}
              className="h-8 text-sm"
              autoFocus
            />
          </div>
        )}
        <button
          type="button"
          aria-pressed={isMassActionActive}
          onClick={onMassActionClick}
          className="mb-1 flex w-full cursor-pointer items-center gap-2 rounded-sm border-b px-2 py-1.5 text-sm hover:bg-accent"
        >
          <span
            aria-hidden="true"
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border',
              isMassActionActive
                ? 'border-primary bg-primary text-primary-foreground'
                : 'opacity-50',
            )}
          >
            {isMassActionActive && <Check className="h-3 w-3" />}
          </span>
          <span className="min-w-0 truncate font-medium">{massActionLabel}</span>
        </button>
        <div className="max-h-60 space-y-1 overflow-y-auto">
          {filteredOptions.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
            >
              <Checkbox
                checked={draftIds.includes(option.id)}
                onCheckedChange={() => toggleTask(option.id)}
              />
              <span title={option.name} className="min-w-0 truncate">
                {option.name}
              </span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
